import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TermsEntity } from '@/entities';
import { isRequiredSatisfied, toAcceptConsentsRequest, toConsentChecklistItem, withHouseRule } from '@/entities/terms';
import { toLangType, type RequiredConsentItem } from '@/entities/terms/types';
import { toast } from '@/hooks/use-toast';
import { getLocale, t } from '@/i18n';
import { useAuth } from '@/router/AuthContext';
import { extractErrorCode } from '@/shared/api/error-handler';
import { localizeError } from '@/shared/api/error-localize';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';

import { useAcceptConsentsMutation } from '../model/query';

import ConsentChecklist from './ConsentChecklist';

/**
 * TRAD-3777(에픽 TRAD-3773 · davidyoon 2026-10-19 «권고안으로 진행») — 하우스 룰(EULA)은 «글·댓글을 쓸 때» 묻는다(3사 공통, 디자인 안 A).
 * 확인 지점(축소 확정, davidyoon 2026-10-19): ① 새 글 쓰기 페이지 진입 3곳(idea·feed·real-estate-community write — HouseRuleGateOnEnter)
 * ② 서버 403 TERMS_008 공통 리스너(HouseRuleRequiredListener — 수정·댓글은 여기서). ~~수정 화면 진입 · 댓글 제출 전 ensureAccepted~~ 는 뺐다.
 *
 * 안 A = 다이얼로그 안에 «제목 + 설명 + 동의 1행(보기 → /house-rule) + 제출». 새 레이아웃 0 — 동의 줄은 가입①·게이트와 같은
 * `ConsentChecklist`, 셸은 살아 있는 `shared/ui/dialog`(JournalComposerPage 확인 Dialog 와 같은 패턴), 기록은 게이트와 같은
 * accept-batch(`useAcceptConsentsMutation`). 기준: docs-ops/2026-10-19-consent-ui-design-audit.md §5 · 시안 TWcGBvXpbDaGK22cDJMfiy.
 *
 * 판정은 `withHouseRule([], status)`(entities/terms/lib/consent.ts) — 미동의면 1행, 동의했으면 0행.
 * ~~상태를 못 읽었으면 0행(조회 실패로 막지 않는다 — G2 와 같은 원칙, 서버 강제 TRAD-3774 가 최종 방어선)~~
 * 🔴 팀장 정정 2026-10-19 — 조회 실패 = «막고 다시 시도»(fail-closed, android 와 같게). 서버 강제(3774)가 플래그 off 로 나가서
 * 지금은 클라가 유일한 관문이다. 실패하면 같은 다이얼로그에 «커뮤니티 이용 규칙을 불러오지 못했어요» + «다시 시도»를 그리고, 닫으면 «취소»다.
 * (팀장 2026-10-19 — ~~auth.terms.load_failed «약관 정보를…»~~ 는 하우스 룰을 «약관»이라 불러서 3사 공통 신규 키로 바꿨다.)
 *
 * 🔴 이 훅이 못 막는 것: 진입 관문이 없는 쓰기(수정·댓글·새 화면) — 그쪽은 서버 403 TERMS_008 이 최종이다. 새 «글쓰기 페이지»를 만들면 여기를 붙인다.
 */
export const useHouseRuleGate = () => {
  const { isAuthenticated } = useAuth();
  const [ask, setAsk] = useState<RequiredConsentItem[]>([]);
  // 상태 조회 실패로 막고 있는 중(fail-closed) — 다이얼로그를 «다시 시도» 모양으로 그린다.
  const [loadFailed, setLoadFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(() => new Set());
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const { mutateAsync: acceptConsents, isPending } = useAcceptConsentsMutation();
  const items = useMemo(() => ask.map(toConsentChecklistItem), [ask]);

  /** null = 조회 실패(막는다). [] = 동의됨. [EULA] = 물어본다. */
  const loadAsk = useCallback(async (): Promise<RequiredConsentItem[] | null> => {
    try {
      const status = await TermsEntity.TermsRepository.getTermsAcceptStatus('EULA', toLangType(getLocale()));
      return withHouseRule([], status);
    } catch (error) {
      console.error('[HouseRuleGate] house rule status failed — blocking (retry)', error);
      return null;
    }
  }, []);

  const settle = useCallback((ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setAsk([]);
    setLoadFailed(false);
    setChecked(new Set());
  }, []);

  /** 동의돼 있으면(또는 비로그인) 바로 true. 미동의·조회 실패면 다이얼로그를 띄우고 동의 → true / 닫기 → false. */
  const ensureAccepted = useCallback(async (): Promise<boolean> => {
    // 비로그인 안내는 호출부 몫(글쓰기 페이지는 /login 으로). 인증 API 를 세션 없이 부르지 않는다.
    if (!isAuthenticated) return true;
    const next = await loadAsk();
    if (next !== null && next.length === 0) return true;
    return new Promise<boolean>(resolve => {
      resolver.current = resolve;
      if (next === null) setLoadFailed(true);
      else setAsk(next);
    });
  }, [isAuthenticated, loadAsk]);

  const handleRetry = async () => {
    setRetrying(true);
    const next = await loadAsk();
    setRetrying(false);
    if (next === null) return; // 계속 막는다(로그는 loadAsk 가 남겼다)
    setLoadFailed(false);
    if (next.length === 0) settle(true);
    else setAsk(next);
  };

  const handleAccept = async () => {
    if (!isRequiredSatisfied(items, checked)) return;
    try {
      await acceptConsents(toAcceptConsentsRequest(items, checked));
      settle(true);
    } catch (err) {
      console.error('[HouseRuleGate] accept failed', err);
      toast({ description: localizeError(err, t('auth.terms.toast.accept_failed')) });
      // TERMS_004(버전이 바뀜) — 지금 버전으로 다시 그린다
      if (extractErrorCode(err) === 'TERMS_004') {
        setChecked(new Set());
        const next = await loadAsk();
        if (next === null) {
          setAsk([]);
          setLoadFailed(true);
        } else if (next.length === 0) settle(true);
        else setAsk(next);
      }
    }
  };

  const dialog = (
    <Dialog
      open={ask.length > 0 || loadFailed}
      onOpenChange={open => {
        if (!open && !isPending) settle(false);
      }}
    >
      <DialogContent data-testid="house-rule-gate">
        <DialogHeader>
          <DialogTitle>{t('consent.house_rule_gate.title')}</DialogTitle>
          <DialogDescription>{t('consent.house_rule_gate.body')}</DialogDescription>
        </DialogHeader>
        {loadFailed ? (
          <>
            <p className="text-sm text-muted-foreground" role="alert">
              {t('consent.house_rule_gate.load_failed')}
            </p>
            <Button onClick={handleRetry} loading={retrying} className="w-full">
              {t('error.generic.retry')}
            </Button>
          </>
        ) : (
          <>
            <ConsentChecklist items={items} checked={checked} onChange={setChecked} disabled={isPending} />
            <Button onClick={handleAccept} loading={isPending} disabled={!isRequiredSatisfied(items, checked)} className="w-full">
              {t('consent.continue')}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  return { ensureAccepted, dialog };
};

/**
 * 새 글 쓰기 «화면에 들어올 때» 묻는다. 닫으면(`onCancel`) 호출부가 목록으로 되돌린다. ~~수정 보기는 상세로~~(수정 관문은 뺐다).
 * 마운트당 한 번만 묻는다.
 */
export const HouseRuleGateOnEnter = ({ onCancel }: { onCancel: () => void }) => {
  const { ensureAccepted, dialog } = useHouseRuleGate();
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    let alive = true;
    void ensureAccepted().then(ok => {
      if (alive && !ok) onCancelRef.current();
    });
    return () => {
      alive = false;
    };
    // 마운트 1회 — ensureAccepted 가 인증 상태로 다시 만들어져도 다시 묻지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return dialog;
};
